/**
 * 🔴 PH-52 — the line that stands where the closed sprints are, and the control that brings them back.
 *
 * ⭐⭐ IT LIVES IN THE LIST, NOT IN THE TOOLBAR, and that is a considered choice:
 *   · The toolbar is already at its 390px budget. `SprintBacklogPage` says so in its own comment —
 *     `New sprint` + `Expand all` + `Collapse all` "fit on one line" at that width, and a fourth
 *     button is the one that breaks it. A control that forces the toolbar to wrap costs a row of
 *     vertical space on every project, including the ones with no closed sprint at all.
 *   · A toolbar toggle can only say "Show closed". This one says HOW MANY and stands WHERE THEY WENT,
 *     which is the whole question a reader has when a section they remember is missing.
 *   · Tapping it puts the revealed sections directly BELOW it — under the finger — rather than
 *     somewhere the reader then has to go and find.
 *
 * ⛔ AND IT IS NOT IN THE FILTER BAR. Everything in that bar becomes a query parameter of
 *   `GET /tickets`. This changes what is DRAWN and nothing about what is asked for; putting it there
 *   would say the opposite.
 *
 * ⚠️ ONE LABEL AT EVERY WIDTH — the rule `SprintSection` already states. The count is inside the
 *   accessible name because the count is the information, not decoration.
 */
export function ClosedSprintsToggle({
  count,
  show,
  onToggle,
}: {
  /** How many closed sections the toggle governs. The caller draws nothing when this is 0. */
  count: number;
  show: boolean;
  onToggle: (show: boolean) => void;
}) {
  const plural = count === 1 ? '' : 's';

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] px-3 py-2 sm:px-4">
      <button
        type="button"
        onClick={() => onToggle(!show)}
        className="h-9 shrink-0 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2.5 text-[12px] font-semibold whitespace-nowrap"
      >
        {show ? `Hide ${count} closed sprint${plural}` : `Show ${count} closed sprint${plural}`}
      </button>

      {/* ⭐ The count survives even while the sections do not, so "we have shipped two" is never a
          thing the reader has to press a button to find out. */}
      <span className="min-w-0 text-[11.5px] text-[color:var(--color-ink-muted)]">
        {show
          ? 'Closed sprints are shown. They are hidden by default so this board does not grow a section per sprint forever.'
          : `${count} closed sprint${plural} ${count === 1 ? 'is' : 'are'} hidden — their tickets are still in them, and still offered wherever a sprint has to be picked.`}
      </span>
    </div>
  );
}
